// "RFQ to best offer", run end to end as a demo. A purchase order arrives as a Salesforce-style
// case; Elariom reads it into lanes and legs (trucking to the port, ocean, trucking to the door),
// chooses truckers for each trucking leg, carriers to search online for the ocean leg and
// forwarders for the whole job end to end, drafts the RFQs and asks for approval, "sends" them,
// collects the replies (and reminds the one who is late), then compares door-to-door options
// (own legs vs end to end) with pros, cons and a decision. Claude does the reading, choosing,
// drafting and weighing (POST /api/admin/elariom/:org/rfq-demo/plan, …/replies, …/decision);
// the suppliers, carrier rates and replies are made up and nothing is emailed. The page says so.

// React imports
import { useEffect, useRef, useState } from 'react';

// Other imports
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faChevronDown, faEnvelope, faClock, faBell, faBriefcase, faPaperclip, faBolt, faTruck, faShip, faGlobe, faArrowRight, faPlus, faMinus, faPlane } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import SupplierLogos from '@monada-ai/monada-shipping-companies-logos';

import { Pill } from './ElariomParts.js';
import { INK, MUTED, HAIRLINE, SURFACE, GOOD, WARN, BAD, SERIF, dotted } from './elariomTheme.js';
// Elariom's reading of the sample case, prepared ahead so the demo doesn't wait for it.
import SAMPLE_READING from './rfqDemoSample.json';

const STAGES = [
    { id: 'ticket', label: 'Request or PO arrives', kind: 'Trigger' },
    { id: 'understand', label: 'Understand the request', kind: 'Agent' },
    { id: 'choose', label: 'Choose who to ask', kind: 'Agent' },
    { id: 'draft', label: 'Draft the RFQs', kind: 'Agent' },
    { id: 'send', label: 'Approve & send', kind: 'Your approval' },
    { id: 'replies', label: 'Replies & follow-up', kind: 'Agent' },
    { id: 'compare', label: 'Compare & choose', kind: 'Your choice' },
    { id: 'done', label: 'Save & close the loop', kind: 'Action' },
];

const CASE = {
    number: '00048213',
    subject: 'PO 7781-0457: Proalim drinks and oils, Tatuí → Ridderkerk',
    emailSubject: 'PO 7781-0457: 4 x 40\' HC Proalim products door to door from Tatuí, prices needed by Friday',
    contact: 'Sanne de Vries',
    account: 'Hollandia Foods B.V.',
    email: 'logistics@hollandiafoods.example',
    attachment: 'PO_7781-0457.pdf',
};

const SAMPLE_TICKET = `Hi team,

Please quote door to door for our PO 7781-0457 from Proalim, 4 x 40' HC.

Pick-up: Proalim's plant, Rodovia Antonio Romano Schincariol km 118, Tatuí – SP, Brazil. Loading Mon–Fri 07:00–16:00.
Delivery: our DC at Nijverheidsweg 12, Ridderkerk, Netherlands (near Rotterdam). Unloading by appointment.

Cargo, palletised, about 21,000 kg per container:
- 1 x 40' HC essential oils in drums (non-hazardous, flash point above 60 °C, MSDS attached)
- 2 x 40' HC protein drinks and botanical sodas (cans and PET bottles)
- 1 x 40' HC mocktail and cocktail bases (glass bottles, fragile)
The drinks must not freeze: it arrives in December, so please include thermal liners or tell us the options.

Ready 3 November. Terms: EXW Tatuí, delivered DAP Ridderkerk.

We need prices by Friday. Please make sure we get enough free time in Rotterdam, last time we paid a lot of demurrage.

Thanks,
Sanne de Vries
Logistics Manager, Hollandia Foods B.V.`;

const LEG = {
    'pre-carriage': { label: 'Origin trucking', icon: faTruck },
    ocean: { label: 'Ocean', icon: faShip },
    air: { label: 'Air', icon: faPlane },
    'on-carriage': { label: 'Destination trucking', icon: faTruck },
    'end-to-end': { label: 'End to end', icon: faGlobe },
};

const money = (n, c = 'USD') => `${c} ${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const pause = ms => new Promise(r => setTimeout(r, ms));
const errorOf = e => e.response?.data?.detail || e.message;
const priceVs = p => (p < 1 ? `−${Math.round((1 - p) * 100)}%` : p > 1 ? `+${Math.round((p - 1) * 100)}%` : 'avg');

function Label({ children }) {
    return <Typography sx={{ fontSize: '10.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>{children}</Typography>;
}

function H({ children, sub }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '28px', fontWeight: 600, lineHeight: 1.15, color: INK }}>{children}</Typography>
            {sub && <Typography sx={{ fontSize: '12.5px', color: MUTED, maxWidth: '760px' }}>{sub}</Typography>}
        </Box>
    );
}

function H2({ children, right }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '19px', fontWeight: 600, color: INK }}>{children}</Typography>
            {right && <Typography sx={{ fontSize: '12px', color: MUTED }}>{right}</Typography>}
        </Box>
    );
}

function Working({ lines, every = 5000 }) {
    // A short checklist that ticks along while Claude works, so a minute of waiting reads as progress.
    const [ n, setN ] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setN(x => Math.min(x + 1, lines.length - 1)), every);
        return () => clearInterval(t);
    }, [lines.length, every]);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '20px 0' }}>
            {lines.map((l, i) => (
                <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: i <= n ? INK : alpha(INK, 0.35) }}>
                    {i < n ? <FontAwesomeIcon icon={faCheck} style={{ color: GOOD, width: '14px' }} /> : i === n ? <CircularProgress size={13} thickness={5} /> : <Box sx={{ width: '14px' }} />}
                    {l}
                </Box>
            ))}
        </Box>
    );
}

function LogLines({ lines }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', borderLeft: `1px solid ${HAIRLINE}`, paddingLeft: '12px' }}>
            {lines.map((l, i) => (
                <Box key={i} sx={{ display: 'flex', gap: '8px', fontSize: '12px', color: MUTED, lineHeight: 1.5, animation: 'rfqLogIn .25s ease-out', '@keyframes rfqLogIn': { from: { opacity: 0, transform: 'translateY(-2px)' }, to: { opacity: 1, transform: 'none' } } }}>
                    <Box component='span' sx={{ color: alpha(INK, 0.35), fontVariantNumeric: 'tabular-nums' }}>{String(i + 1).padStart(2, '0')}</Box>
                    <Box component='span' sx={{ color: INK }}>{l}</Box>
                </Box>
            ))}
        </Box>
    );
}

// Reading the case: the first two steps finish when the request comes back read, with its log shown
// line by line under the splitting step; the rest tick along while suppliers are chosen and drafted.
function PlanProgress({ understood, shownLog, finished }) {
    const later = ['Finding forwarders', 'Finding truckers per leg', 'Choosing carriers and airlines', 'Drafting the RFQs'];
    const logDone = understood && shownLog >= understood.log.length;
    const [ n, setN ] = useState(0);
    useEffect(() => {
        if (!logDone) return undefined;
        const t = setInterval(() => setN(x => Math.min(x + 1, later.length - 1)), 5000);
        return () => clearInterval(t);
    }, [logDone, later.length]);
    const row = (text, state) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: state === 'todo' ? alpha(INK, 0.35) : INK }}>
            {state === 'done' ? <FontAwesomeIcon icon={faCheck} style={{ color: GOOD, width: '14px' }} /> : state === 'now' ? <CircularProgress size={13} thickness={5} /> : <Box sx={{ width: '14px' }} />}
            {text}
        </Box>
    );
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '20px 0' }}>
            {row('Reading the request', understood ? 'done' : 'now')}
            {row('Mapping routes and legs', logDone ? 'done' : understood ? 'now' : 'todo')}
            {understood && <Box sx={{ marginLeft: '24px', maxWidth: '760px' }}><LogLines lines={understood.log.slice(0, shownLog)} /></Box>}
            {later.map((l, i) => <Box key={l}>{row(l, finished ? 'done' : !logDone ? 'todo' : i < n ? 'done' : i === n ? 'now' : 'todo')}</Box>)}
        </Box>
    );
}

// Carrier and airline logos: Monada's logo set for the big ocean carriers; anything else (other
// carriers, airlines) gets a small badge with its code.
const LOGO_KEYS = { MAERSK: 'MAERSK', MSC: 'MSC', 'CMA CGM': 'CMA_CGM', 'HAPAG-LLOYD': 'HAPAG_LLOYD', 'HAPAG LLOYD': 'HAPAG_LLOYD', ONE: 'ONE', COSCO: 'COSCO', EVERGREEN: 'EVERGREEN', ZIM: 'ZIM', OOCL: 'OOCL' };
const CODES = {
    HMM: 'HMM', 'YANG MING': 'YML', 'LATAM CARGO': 'LA', 'KLM MARTINAIR CARGO': 'KL', 'LUFTHANSA CARGO': 'LH', 'AIR FRANCE CARGO': 'AF',
    'QATAR AIRWAYS CARGO': 'QR', 'EMIRATES SKYCARGO': 'EK', 'TURKISH CARGO': 'TK', CARGOLUX: 'CV', 'ATLAS AIR': '5Y', 'IAG CARGO': 'IAG',
    'ETHIOPIAN CARGO': 'ET', 'AZUL CARGO': 'AD', 'AVIANCA CARGO': 'AV', 'TAP AIR CARGO': 'TP', 'CATHAY CARGO': 'CX',
};
const carrierKey = name => {
    const n = String(name || '').toUpperCase().trim();
    // Exact name, or a longer one that starts with it ("MSC Mediterranean …").
    const prefix = Object.keys(LOGO_KEYS).find(k => n.startsWith(`${k} `));
    return LOGO_KEYS[n] || (prefix ? LOGO_KEYS[prefix] : null);
};
const isCarrier = name => !!carrierKey(name) || !!CODES[String(name || '').toUpperCase().trim()];

function CarrierMark({ name, showName = false, size = 'mini' }) {
    const key = carrierKey(name);
    const px = size === 'mini' ? 20 : 29;
    const mark = key
        ? <SupplierLogos size={size === 'mini' ? SupplierLogos.size.MINI : SupplierLogos.size.NORMAL} organization={SupplierLogos.organizations[key]} />
        : (
            <Box sx={{ width: `${px}px`, height: `${px}px`, borderRadius: '4px', border: `1px solid ${INK}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: (CODES[String(name).toUpperCase().trim()] || '').length > 2 ? '7.5px' : '9px', fontWeight: 700, color: INK, background: '#FFFFFF', flexShrink: 0 }}>
                {CODES[String(name || '').toUpperCase().trim()] || String(name || '?').slice(0, 2).toUpperCase()}
            </Box>
        );
    return (
        <Box component='span' title={name} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', verticalAlign: 'middle', '& img': { display: 'block' } }}>
            {mark}
            {showName && <Box component='span'>{name}</Box>}
        </Box>
    );
}

function Facts({ items }) {
    // Label above value, side by side: the job at a glance.
    return (
        <Box sx={{ display: 'flex', gap: '10px 26px', flexWrap: 'wrap' }}>
            {items.filter(([, v]) => v).map(([k, v]) => (
                <Box key={k} sx={{ minWidth: 0 }}>
                    <Label>{k}</Label>
                    <Typography sx={{ fontSize: '13px' }}>{v}</Typography>
                </Box>
            ))}
        </Box>
    );
}

function Primary({ children, ...props }) {
    return <Button variant='contained' disableElevation sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '13px', padding: '8px 18px' }} {...props}>{children}</Button>;
}

function Expandable({ title, children, defaultOpen = false }) {
    const [ open, setOpen ] = useState(defaultOpen);
    return (
        <Box>
            <Box role='button' tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={e => e.key === 'Enter' && setOpen(!open)}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: MUTED, cursor: 'pointer', '&:hover': { color: INK } }}>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} /> {title}
            </Box>
            <Collapse in={open}>{children}</Collapse>
        </Box>
    );
}

const day = v => (v ? dayjs(v).format('D MMM') : '—');
const days = n => `${n} day${Number(n) === 1 ? '' : 's'}`;
// How far a date is from today: "in 5 days", "7 days ago".
const fromNow = v => {
    const n = dayjs(v).startOf('day').diff(dayjs().startOf('day'), 'day');
    if (n === 0) return 'today';
    if (n === 1) return 'tomorrow';
    if (n === -1) return 'yesterday';
    return n > 0 ? `in ${n} days` : `${-n} days ago`;
};
// A date with how far it is from today, e.g. "ETD 13 Nov · in 5 days".
const When = ({ label, d }) => (d
    ? <>{label ? `${label} ` : ''}{day(d)} <Box component='span' sx={{ color: MUTED, fontWeight: 400 }}>· {fromNow(d)}</Box></>
    : '—');
// Example dates, counted from today so the demo always looks current.
const inDays = n => dayjs().add(n, 'day').format('YYYY-MM-DD');

// ---------- 1. The case ----------

// The request as it arrives in the customer's CRM: a Salesforce-style Service Cloud case, with the
// customer's email in the case feed. Only the look is borrowed (no logo); the email text is editable.
const SF = { blue: '#0176D3', ink: '#181818', muted: '#706E6B', line: '#DDDBDA', canvas: '#F3F3F3', font: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' };

function SfField({ label, children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Typography sx={{ fontSize: '12px', color: SF.muted }}>{label}</Typography>
            <Typography component='div' sx={{ fontSize: '13px', color: SF.ink }}>{children}</Typography>
        </Box>
    );
}

function SfButton({ children, brand, ...props }) {
    return (
        <Box component='button' type='button' {...props} sx={{
            fontSize: '13px', lineHeight: '30px', padding: '0 14px', borderRadius: '4px', cursor: 'pointer',
            border: `1px solid ${brand ? SF.blue : SF.line}`, background: brand ? SF.blue : '#FFFFFF', color: brand ? '#FFFFFF' : SF.blue,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap',
            '&:hover': { background: brand ? '#014486' : '#F3F3F3' }, '&:disabled': { opacity: 0.5, cursor: 'default' },
        }}>
            {children}
        </Box>
    );
}

function TicketStage({ ticket, setTicket, workspaceTitle, onRun }) {
    const domain = (workspaceTitle || 'workspace').toLowerCase().replace(/[^a-z0-9]+/g, '');
    const received = dayjs().subtract(3, 'minute');
    const card = { background: '#FFFFFF', border: `1px solid ${SF.line}`, borderRadius: '4px', boxShadow: '0 2px 2px rgba(0,0,0,0.1)' };
    const link = { color: SF.blue };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <H>New case in Salesforce</H>
            <Box sx={{ border: `1px solid ${SF.line}`, borderRadius: '6px', overflow: 'hidden', background: SF.canvas, boxShadow: `0 10px 30px ${alpha(INK, 0.1)}`, '&, & .MuiTypography-root, & button, & textarea, & input, & div': { fontFamily: `${SF.font} !important` } }}>
                {/* Console bar with the open case tab */}
                <Box sx={{ display: 'flex', alignItems: 'stretch', background: '#FFFFFF', borderBottom: `3px solid ${SF.blue}`, height: '40px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 16px', borderRight: `1px solid ${SF.line}` }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 3px)', gap: '2px' }}>{Array.from({ length: 9 }).map((_, i) => <Box key={i} sx={{ width: '3px', height: '3px', borderRadius: '50%', background: SF.muted }} />)}</Box>
                        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: SF.ink }}>Service Console</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '0 14px', fontSize: '13px', color: SF.muted, borderRight: `1px solid ${SF.line}` }}>Cases</Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 14px', fontSize: '13px', color: SF.ink, background: alpha(SF.blue, 0.06), borderTop: `3px solid ${SF.blue}`, marginBottom: '-3px' }}>
                        <FontAwesomeIcon icon={faBriefcase} style={{ color: '#F88962', fontSize: '11px' }} /> {CASE.number}
                        <FontAwesomeIcon icon={faXmark} style={{ color: SF.muted, fontSize: '10px' }} />
                    </Box>
                </Box>
                <Box sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Highlights panel */}
                    <Box sx={{ ...card, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Box sx={{ width: '32px', height: '32px', borderRadius: '4px', background: '#F88962', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '15px' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: '200px' }}>
                                <Typography sx={{ fontSize: '12px', color: SF.muted }}>Case</Typography>
                                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: SF.ink, lineHeight: 1.25 }}>{CASE.number} · {CASE.subject}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', '& > button:not(:first-of-type)': { marginLeft: '-1px' } }}>
                                <SfButton>Follow</SfButton>
                                <SfButton>Change Owner</SfButton>
                                <SfButton>Close Case</SfButton>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, auto)' }, gap: '12px 28px', justifyContent: 'start' }}>
                            <SfField label='Priority'><Box component='span' sx={{ color: '#BA0517', fontWeight: 600 }}>High</Box></SfField>
                            <SfField label='Status'>New</SfField>
                            <SfField label='Case Origin'>Email</SfField>
                            <SfField label='Case Owner'><Box component='span' sx={link}>Pricing Queue</Box></SfField>
                            <SfField label='Account Name'><Box component='span' sx={link}>{CASE.account}</Box></SfField>
                            <SfField label='Contact Name'><Box component='span' sx={link}>{CASE.contact}</Box></SfField>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 260px' }, gap: '12px', alignItems: 'start' }}>
                        {/* Feed with the customer's email */}
                        <Box sx={card}>
                            <Box sx={{ display: 'flex', gap: '22px', padding: '0 16px', borderBottom: `1px solid ${SF.line}`, fontSize: '14px' }}>
                                {['Feed', 'Details', 'Related'].map((t, i) => (
                                    <Box key={t} sx={{ padding: '10px 0', color: i === 0 ? SF.ink : SF.muted, fontWeight: i === 0 ? 600 : 400, borderBottom: i === 0 ? `3px solid ${SF.blue}` : '3px solid transparent', marginBottom: '-1px' }}>{t}</Box>
                                ))}
                            </Box>
                            <Box sx={{ padding: '14px 16px', display: 'flex', gap: '12px' }}>
                                <Box sx={{ width: '28px', height: '28px', borderRadius: '50%', background: '#939393', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '12px' }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', color: SF.ink }}><Box component='span' sx={link}>{CASE.contact}</Box> sent an email</Typography>
                                        <Typography sx={{ fontSize: '12px', color: SF.muted }}>{received.format('MMMM D, YYYY [at] h:mm A')}</Typography>
                                    </Box>
                                    <Box sx={{ border: `1px solid ${SF.line}`, borderRadius: '4px' }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: '4px', padding: '10px 12px', borderBottom: `1px solid ${SF.line}`, fontSize: '13px', background: '#FAFAF9' }}>
                                            <Box sx={{ color: SF.muted }}>From</Box><Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{CASE.contact} &lt;{CASE.email}&gt;</Box>
                                            <Box sx={{ color: SF.muted }}>To</Box><Box>pricing@{domain}.example</Box>
                                            <Box sx={{ color: SF.muted }}>Subject</Box><Box sx={{ fontWeight: 600 }}>{CASE.emailSubject}</Box>
                                        </Box>
                                        <TextField
                                            id='rfq-demo-ticket'
                                            multiline
                                            fullWidth
                                            minRows={12}
                                            value={ticket}
                                            onChange={e => setTicket(e.target.value)}
                                            variant='standard'
                                            InputProps={{ disableUnderline: true, sx: { padding: '12px', fontSize: '13px', lineHeight: 1.55, color: SF.ink } }}
                                        />
                                        <Box sx={{ padding: '8px 12px', borderTop: `1px solid ${SF.line}`, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                            <FontAwesomeIcon icon={faPaperclip} style={{ color: SF.muted }} />
                                            <Box component='span' sx={link}>{CASE.attachment}</Box>
                                            <Box component='span' sx={{ color: SF.muted }}>162 KB</Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        {/* Case details and the hand-over to Elariom */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <Box sx={{ ...card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: SF.ink }}>Case Details</Typography>
                                <SfField label='Type'>Quote Request</SfField>
                                <SfField label='Case Reason'>New shipment</SfField>
                                <SfField label='Service'>Ocean freight, door to door</SfField>
                                <SfField label='Entitlement'>Respond by Friday</SfField>
                                <SfField label='Web Email'>{CASE.email}</SfField>
                            </Box>
                            <Box sx={{ ...card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FontAwesomeIcon icon={faBolt} style={{ color: SF.blue, fontSize: '12px' }} />
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: SF.ink }}>Elariom</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '12.5px', color: SF.muted }}>Get buying prices from your suppliers for this request.</Typography>
                                <SfButton brand onClick={onRun} disabled={ticket.trim().length < 20}>Send to Elariom</SfButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// ---------- 2. Understand ----------

function LegChain({ legs }) {
    // The lane as a chain of places with the legs between them.
    if (!legs.length) return null;
    const stops = [legs[0].from, ...legs.map(l => l.to)];
    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', flexWrap: 'nowrap', gap: '6px', width: 'max-content' }}>
            {stops.map((stop, i) => (
                <Box key={`${stop}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Box sx={{ border: `1px solid ${INK}`, borderRadius: '6px', padding: '6px 10px', background: '#FFFFFF', fontSize: '12px', maxWidth: '180px' }}>{stop}</Box>
                    {legs[i] && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 4px', minWidth: '110px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: MUTED }}>
                                <FontAwesomeIcon icon={LEG[legs[i].kind]?.icon || faArrowRight} /> {legs[i].id}
                            </Box>
                            <Box sx={{ width: '100%', borderTop: `1.5px ${legs[i].kind === 'ocean' ? 'solid' : 'dashed'} ${INK}`, margin: '3px 0', position: 'relative', '&:after': { content: '""', position: 'absolute', right: '-1px', top: '-4.5px', borderLeft: `6px solid ${INK}`, borderTop: '4px solid transparent', borderBottom: '4px solid transparent' } }} />
                            <Box sx={{ fontSize: '10.5px', color: MUTED }}>{LEG[legs[i].kind]?.label}</Box>
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );
}

// ---------- 3. Choose ----------

const LIST_COLUMNS = '20px minmax(170px, 1fr) minmax(240px, 2.2fr) 58px 58px 44px 40px 48px';

function ListHead() {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: LIST_COLUMNS, gap: '14px', padding: '8px 14px', borderBottom: `1px solid ${HAIRLINE}`, fontSize: '11px', color: MUTED }}>
            <Box /><Box>Supplier</Box><Box>Why</Box><Box>Answers</Box><Box>Replies in</Box><Box>Wins</Box><Box>Jobs</Box><Box>Price</Box>
        </Box>
    );
}

function SupplierRow({ s, on, onToggle, first, recommended, group }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: LIST_COLUMNS, gap: '14px', padding: '11px 14px', alignItems: 'start', borderTop: first ? 'none' : dotted, opacity: on ? 1 : 0.6, fontSize: '12.5px' }}>
            <Checkbox id={`rfq-demo-supplier-${group}-${s.id}`} size='small' checked={on} onChange={onToggle} sx={{ padding: '0' }} />
            <Box>
                <Box sx={{ fontWeight: 600, fontSize: '13px' }}>{s.name}</Box>
                <Box sx={{ fontSize: '11.5px', color: MUTED }}>{s.city}, {s.country}</Box>
                {(recommended || s.promisedWork) && (
                    <Box sx={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {recommended && <Pill color={GOOD} dot>Recommended</Pill>}
                        {s.promisedWork && <Pill color={GOOD}>Promised work</Pill>}
                    </Box>
                )}
            </Box>
            <Box>{s.reason}</Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(s.responseRate * 100)}%</Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.replyHours} h</Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(s.winRate * 100)}%</Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.jobs}</Box>
            <Box sx={{ color: s.priceIndex < 1 ? GOOD : s.priceIndex > 1 ? WARN : INK }}>{priceVs(s.priceIndex)}</Box>
        </Box>
    );
}

function Group({ icon, title, sub, children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}><FontAwesomeIcon icon={icon} style={{ fontSize: '12px' }} /> {title}</Typography>
                {sub && <Typography sx={{ fontSize: '11.5px', color: MUTED }}>{sub}</Typography>}
            </Box>
            <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', background: '#FFFFFF', overflowX: 'auto' }}>
                <Box sx={{ minWidth: '780px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

// Who is asked for what: truckers per trucking leg, carriers searched online per ocean leg, and
// forwarders for each lane end to end.
function Way({ n, title, counts, children }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', borderTop: `1px solid ${INK}`, paddingTop: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
                <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '20px', fontWeight: 600 }}>{n}. {title}</Typography>
                <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{counts.map(c => <Pill key={c} color={INK}>{c}</Pill>)}</Box>
            </Box>
            {children}
        </Box>
    );
}

function CarrierRows({ carriers }) {
    return carriers.map((c, i) => (
        <Box key={c.name} sx={{ display: 'grid', gridTemplateColumns: '20px minmax(170px, 1.1fr) minmax(220px, 3fr)', gap: '14px', padding: '10px 14px', borderTop: i ? dotted : 'none', fontSize: '12.5px' }}>
            <FontAwesomeIcon icon={faGlobe} style={{ color: MUTED, marginTop: '3px' }} />
            <Box sx={{ fontWeight: 600, fontSize: '13px' }}><CarrierMark name={c.name} showName /></Box>
            <Box>{c.reason}</Box>
        </Box>
    ));
}

// The legs of one mode (sea or air) across its routes, each listed once, in route order.
function LegsOfMode({ plan, mode, included, toggle }) {
    const legsById = Object.fromEntries((plan.request.legs || []).map(l => [l.id, l]));
    const routes = (plan.request.routes || []).filter(r => r.mode === mode);
    const legs = [...new Set(routes.flatMap(r => r.legIds))].map(id => legsById[id]).filter(Boolean);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {routes.map(r => (
                <Box key={r.id} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{r.name}</Typography>
                    <Box sx={{ overflowX: 'auto', paddingBottom: '4px' }}><LegChain legs={r.legIds.map(id => legsById[id]).filter(Boolean)} /></Box>
                </Box>
            ))}
            {legs.map(leg => {
                const online = leg.kind === 'ocean' || leg.kind === 'air';
                const title = `${LEG[leg.kind]?.label} · ${leg.from} → ${leg.to}${leg.distanceKm ? ` · ${leg.distanceKm} km` : ''}`;
                if (online) {
                    const carriers = plan.carriers.filter(c => c.legId === leg.id);
                    return carriers.length ? <Group key={leg.id} icon={LEG[leg.kind].icon} title={title} sub='Portal rates, no RFQ'><CarrierRows carriers={carriers} /></Group> : null;
                }
                const truckers = candidatesFor(plan, leg.id);
                return truckers.length ? (
                    <Group key={leg.id} icon={faTruck} title={title} sub={`${truckers.length} cover it`}>
                        <ListHead />
                        {truckers.map((x, i) => <SupplierRow key={x.id} s={x} group={leg.id} recommended={isRecommended(plan, x.id)} on={included.includes(x.id)} onToggle={() => toggle(x.id)} first={i === 0} />)}
                    </Group>
                ) : null;
            })}
        </Box>
    );
}

// Everyone in the network who covers a trucking leg or a lane (end to end), recommended ones first.
const isRecommended = (plan, id) => plan.suppliers.some(x => x.id === id);
function candidatesFor(plan, target) {
    const panel = Object.fromEntries((plan.panel || plan.suppliers).map(x => [x.id, x]));
    const ids = plan.candidates?.[target] || plan.suppliers.filter(x => x.askFor.includes(target)).map(x => x.id);
    return ids.map(id => panel[id]).filter(Boolean).sort((a, b) => Number(isRecommended(plan, b.id)) - Number(isRecommended(plan, a.id)));
}

function ChooseStage({ plan, included, setIncluded, onNext, busy }) {
    const toggle = id => setIncluded(included.includes(id) ? included.filter(x => x !== id) : [...included, id]);
    const r = plan.request;
    const net = plan.network || {};
    const truckersFor = mode => {
        const legIds = new Set((r.routes || []).filter(x => x.mode === mode).flatMap(x => x.legIds));
        return new Set([...legIds].flatMap(id => (plan.candidates?.[id] || []))).size;
    };
    const hasMode = mode => (r.routes || []).some(x => x.mode === mode);
    const lane0 = r.lanes[0];
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <H>Who to ask</H>
                <Facts items={[
                    ['Customer', r.customer],
                    ['Load', r.lanes.map(l => `${l.quantity} × ${l.equipment}`).join(', ')],
                    ['Route', r.lanes.map(l => l.name || `${l.origin} → ${l.destination}`).join(', ')],
                    ['Terms', lane0?.incoterm],
                    ['Ready', lane0?.readyDate && day(lane0.readyDate)],
                    ['Quote by', r.deadline],
                    ['Priority', r.priorities],
                ]} />
                <Typography sx={{ fontSize: '12px', color: MUTED }}>Ticked = recommended by Elariom.</Typography>
                {plan.chooseLog?.length > 0 && <Expandable title='How Elariom chose'><Box sx={{ marginTop: '8px', maxWidth: '760px' }}><LogLines lines={plan.chooseLog} /></Box></Expandable>}
            </Box>
            <Way n={1} title='Forwarders · end to end' counts={[`${net.forwarders ?? '…'} found`]}>
                {r.lanes.map(lane => {
                    const list = candidatesFor(plan, lane.id);
                    return list.length > 0 && (
                        <Group key={lane.id} icon={faGlobe} title={lane.name || `${lane.origin} → ${lane.destination}`}>
                            <ListHead />
                            {list.map((x, i) => <SupplierRow key={x.id} s={x} group={lane.id} recommended={isRecommended(plan, x.id)} on={included.includes(x.id)} onToggle={() => toggle(x.id)} first={i === 0} />)}
                        </Group>
                    );
                })}
            </Way>
            {hasMode('ocean') && (
                <Way n={2} title='Direct · sea' counts={[`${truckersFor('ocean')} truckers`, `${net.carriers ?? 10} carriers connected`]}>
                    <LegsOfMode plan={plan} mode='ocean' included={included} toggle={toggle} />
                </Way>
            )}
            {hasMode('air') && (
                <Way n={hasMode('ocean') ? 3 : 2} title='Direct · air' counts={[`${truckersFor('air')} truckers`, `${net.airlines ?? 77} airlines connected`]}>
                    <LegsOfMode plan={plan} mode='air' included={included} toggle={toggle} />
                </Way>
            )}
            <Primary onClick={onNext} disabled={!included.length || busy}>{busy ? 'Drafting…' : 'Draft the RFQs'}</Primary>
        </Box>
    );
}

// ---------- 4. Draft & approve ----------

function DraftStage({ plan, included, drafts, setDrafts, onApprove }) {
    const list = (plan.panel || plan.suppliers).filter(s => included.includes(s.id));
    const [ tab, setTab ] = useState(list[0]?.id);
    const s = list.find(x => x.id === tab) || list[0];
    const d = drafts[s?.id] || { subject: '', body: '' };
    const legs = plan.request.legs || [];
    const forWhat = x => x.askFor.map(id => legs.find(l => l.id === id)?.kind || 'end-to-end').map(k => LEG[k]?.label || k).join(', ');
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <H>RFQs to approve</H>
            <Tabs value={s?.id} onChange={(e, v) => setTab(v)} variant='scrollable' sx={{ minHeight: '36px', borderBottom: `1px solid ${HAIRLINE}` }}>
                {list.map(x => <Tab key={x.id} value={x.id} label={x.name} sx={{ textTransform: 'none', fontSize: '12.5px', minHeight: '36px' }} />)}
            </Tabs>
            {s && (
                <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', background: '#FFFFFF', maxWidth: '860px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '70px 1fr', rowGap: '6px', padding: '12px 16px', borderBottom: dotted, fontSize: '12.5px', alignItems: 'center' }}>
                        <Box sx={{ color: MUTED }}>To</Box><Box>{s.contact} &lt;{s.email}&gt; <Box component='span' sx={{ color: MUTED, marginLeft: '6px' }}>· {forWhat(s)}</Box></Box>
                        <Box sx={{ color: MUTED }}>Subject</Box>
                        <TextField
                            id={`rfq-demo-subject-${s.id}`}
                            variant='standard'
                            value={d.subject}
                            onChange={e => setDrafts({ ...drafts, [s.id]: { ...d, subject: e.target.value } })}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '12.5px', fontWeight: 600 } }}
                        />
                    </Box>
                    <TextField
                        id={`rfq-demo-body-${s.id}`}
                        multiline
                        fullWidth
                        minRows={10}
                        variant='standard'
                        value={d.body}
                        onChange={e => setDrafts({ ...drafts, [s.id]: { ...d, body: e.target.value } })}
                        InputProps={{ disableUnderline: true, sx: { padding: '14px 16px', fontSize: '12.5px', lineHeight: 1.6 } }}
                    />
                </Box>
            )}
            {plan.carriers.length > 0 && (
                <Typography sx={{ fontSize: '12px', color: MUTED, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faGlobe} /> Ocean and air, portal rates, no RFQ:
                    <Box component='span' sx={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>{plan.carriers.map(c => c.name).filter((x, i, a) => a.indexOf(x) === i).map(n => <CarrierMark key={n} name={n} />)}</Box>
                </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', border: `1px solid ${INK}`, borderRadius: '8px', padding: '12px 16px', background: alpha(WARN, 0.06), maxWidth: '860px' }}>
                <Box sx={{ flex: 1, minWidth: '240px' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{list.length} RFQ{list.length === 1 ? '' : 's'} ready</Typography>
                    <Typography sx={{ fontSize: '12px', color: MUTED }}>Nothing is emailed in this demo.</Typography>
                </Box>
                <Primary onClick={onApprove} disabled={!list.length}>Approve & send</Primary>
            </Box>
        </Box>
    );
}

// ---------- 5. Send ----------

function SendStage({ list, carriers, sent, searched }) {
    const row = { display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0', borderBottom: dotted, fontSize: '13px' };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <H>Sending</H>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: '28px', maxWidth: '980px' }}>
                <Box>
                    <Label>RFQs</Label>
                    {list.map(s => (
                        <Box key={s.id} sx={row}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ color: MUTED, width: '14px' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>{s.name}<Typography component='span' sx={{ fontSize: '11.5px', color: MUTED, marginLeft: '8px' }}>{s.email}</Typography></Box>
                            {sent[s.id] ? <Box sx={{ color: GOOD, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}><FontAwesomeIcon icon={faCheck} /> Sent {sent[s.id]}</Box> : <CircularProgress size={13} thickness={5} />}
                        </Box>
                    ))}
                </Box>
                {carriers.length > 0 && (
                    <Box>
                        <Label>Portals</Label>
                        {carriers.map(c => (
                            <Box key={`${c.name}-${c.legId}`} sx={row}>
                                <FontAwesomeIcon icon={faGlobe} style={{ color: MUTED, width: '14px' }} />
                                <Box sx={{ flex: 1 }}><CarrierMark name={c.name} showName /></Box>
                                {searched[c.name] ? <Box sx={{ color: GOOD, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}><FontAwesomeIcon icon={faCheck} /> Checked</Box> : <CircularProgress size={13} thickness={5} />}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ---------- 6. Replies: is every way priced? ----------

const arrivedIn = events => o => (o.online ? events.online : events.arrived.includes(o.supplierId));
const MAIN = k => k === 'ocean' || k === 'air';
const minTotal = list => (list.length ? Math.min(...list.map(o => o.total)) : null);

// Where each lane stands: for every way (forwarders end to end, and each direct route by its legs)
// how many asked have answered, the lowest price so far per leg and in total, and who is still
// being chased. Built from the replies that have arrived so far.
function coverageOf({ plan, list, replies, events }) {
    const legsById = Object.fromEntries((plan.request.legs || []).map(l => [l.id, l]));
    const arrived = arrivedIn(events);
    const lateId = replies?.late?.id;
    const waitingOn = ids => ids.filter(id => !events.arrived.includes(id)).map(id => ({
        id, name: list.find(x => x.id === id)?.name || id, chased: id === lateId && events.reminded,
    }));
    return plan.request.lanes.map(lane => {
        const c = replies?.comparison.find(x => x.lane.id === lane.id);
        const ways = [];
        const forwarders = list.filter(x => x.type === 'forwarder' && x.askFor.includes(lane.id)).map(x => x.id);
        if (forwarders.length) {
            const offers = (c?.options || []).filter(o => o.kind === 'end-to-end').map(o => o.parts[0].offer).filter(arrived);
            ways.push({
                id: `fw-${lane.id}`, family: 'forwarder', name: 'Forwarders · end to end',
                endToEnd: { asked: forwarders.length, answered: new Set(offers.map(o => o.supplierId)).size, from: minTotal(offers), offers: offers.length },
                total: minTotal(offers),
                waiting: waitingOn(forwarders),
            });
        }
        (plan.request.routes || []).filter(r => r.laneId === lane.id).forEach(r => {
            const legs = r.legIds.map(id => legsById[id]).filter(Boolean).map(leg => {
                const all = (c?.legOffers?.[leg.id] || []);
                const got = all.filter(arrived);
                if (MAIN(leg.kind)) {
                    const asked = plan.carriers.filter(x => x.legId === leg.id).length;
                    return { leg, online: true, asked, answered: events.online ? got.length : 0, from: minTotal(got), searching: !events.online };
                }
                const truckers = list.filter(x => x.type === 'trucker' && x.askFor.includes(leg.id)).map(x => x.id);
                return { leg, asked: truckers.length, answered: new Set(got.map(o => o.supplierId)).size, from: minTotal(got), waiting: waitingOn(truckers) };
            });
            const priced = legs.every(l => l.from != null);
            ways.push({
                id: r.id, family: r.mode, name: `${r.mode === 'air' ? 'Air' : 'Sea'} ${r.name.replace(/^(Ocean|Air)\s+/i, '')}`,
                legs,
                total: priced ? legs.reduce((sum, l) => sum + l.from, 0) : null,
                waiting: legs.flatMap(l => l.waiting || []),
            });
        });
        const waiting = _.uniqBy(ways.flatMap(w => w.waiting), 'id');
        return { lane, ways, waiting, allPriced: ways.every(w => w.total != null), complete: !!replies && !waiting.length && events.online };
    });
}

function Cell({ data }) {
    // One leg (or the whole job) of one way: answers so far and the lowest price.
    if (!data) return <Box sx={{ color: alpha(INK, 0.3) }}>—</Box>;
    if (data.searching) return <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: MUTED }}><CircularProgress size={11} thickness={5} /> Checking</Box>;
    const done = data.answered >= data.asked;
    return (
        <Box>
            <Box sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{data.from != null ? `from ${money(data.from)}` : <Box component='span' sx={{ color: MUTED, fontWeight: 400 }}>—</Box>}</Box>
            <Box sx={{ fontSize: '11px', color: done ? GOOD : MUTED }}>
                {data.online ? `${data.answered} rate${data.answered === 1 ? '' : 's'}` : `${data.answered}/${data.asked} replied`}
            </Box>
        </Box>
    );
}

const COVER_COLUMNS = 'minmax(190px, 1.4fr) repeat(3, minmax(120px, 1fr)) 120px minmax(150px, 1.1fr)';

// The lanes as a table: one row per way, one column per leg, the total so far, and what is missing.
function CoverageView({ plan, list, replies, events }) {
    const lanes = coverageOf({ plan, list, replies, events });
    const head = { fontSize: '11px', color: MUTED };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {lanes.map(({ lane, ways, waiting, allPriced, complete }) => (
                <Box key={lane.id} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
                        border: `1px solid ${complete ? GOOD : allPriced ? INK : HAIRLINE}`, background: complete ? alpha(GOOD, 0.07) : '#FFFFFF',
                    }}>
                        <FontAwesomeIcon icon={complete ? faCheck : allPriced ? faCheck : faClock} style={{ color: complete ? GOOD : allPriced ? INK : MUTED }} />
                        <Box sx={{ flex: 1 }}>
                            <b>{complete ? 'Ready to decide' : allPriced ? 'Every way priced' : 'Not ready'}</b>
                            <Box component='span' sx={{ color: MUTED }}>{complete ? ' · everyone replied' : ` · waiting on ${waiting.length}${waiting.some(w => w.chased) ? ' · reminder sent' : ' · auto follow-up on'}`}</Box>
                        </Box>
                    </Box>
                    <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', background: '#FFFFFF', overflowX: 'auto' }}>
                        <Box sx={{ minWidth: '880px' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: COVER_COLUMNS, gap: '12px', padding: '8px 14px', borderBottom: `1px solid ${HAIRLINE}` }}>
                                <Box sx={head}>Way</Box><Box sx={head}>Origin trucking</Box><Box sx={head}>Main leg</Box><Box sx={head}>Destination trucking</Box><Box sx={{ ...head, textAlign: 'right' }}>Total from</Box><Box sx={head}>Waiting on</Box>
                            </Box>
                            {ways.map((w, i) => {
                                const leg = k => w.legs?.find(l => (k === 'main' ? MAIN(l.leg.kind) : l.leg.kind === k));
                                // Cheapest and most expensive among the ways priced so far (two or more).
                                const priced = ways.filter(x => x.total != null);
                                const cheapest = priced.length > 1 && w.total === Math.min(...priced.map(x => x.total));
                                const dearest = priced.length > 1 && w.total === Math.max(...priced.map(x => x.total));
                                return (
                                    <Box key={w.id} sx={{ display: 'grid', gridTemplateColumns: COVER_COLUMNS, gap: '12px', padding: '10px 14px', borderTop: i ? dotted : 'none', fontSize: '12.5px', alignItems: 'start' }}>
                                        <Box>
                                            <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px' }}><FontAwesomeIcon icon={w.family === 'forwarder' ? faGlobe : w.family === 'air' ? faPlane : faShip} style={{ fontSize: '11px' }} /> {w.name}</Box>
                                            {w.legs && <Box sx={{ fontSize: '11px', color: MUTED }}>{[w.legs[0]?.leg.from, ...w.legs.map(l => l.leg.to)].join(' → ')}</Box>}
                                            {(cheapest || dearest) && (
                                                <Box sx={{ marginTop: '4px' }}>
                                                    {cheapest && <Pill color={GOOD}>Cheapest</Pill>}
                                                    {dearest && <Pill color={BAD}>Most expensive</Pill>}
                                                </Box>
                                            )}
                                        </Box>
                                        {w.endToEnd
                                            ? <Box sx={{ gridColumn: 'span 3' }}><Cell data={w.endToEnd} /><Box sx={{ fontSize: '11px', color: MUTED }}>All-in, door to door</Box></Box>
                                            : <>
                                                <Box><Cell data={leg('pre-carriage')} /></Box>
                                                <Box><Cell data={leg('main')} /></Box>
                                                <Box><Cell data={leg('on-carriage')} /></Box>
                                            </>}
                                        <Box sx={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{w.total != null ? money(w.total) : <Box component='span' sx={{ color: MUTED, fontWeight: 400 }}>—</Box>}</Box>
                                        <Box sx={{ fontSize: '12px', color: w.waiting.length ? WARN : GOOD }}>
                                            {w.waiting.length
                                                ? w.waiting.map(x => <Box key={x.id}>{x.name}{x.chased ? ' · reminded' : ''}</Box>)
                                                : '—'}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

function RepliesStage({ plan, list, replies, events, error, onCompare }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <H>Replies</H>
            {error && <Typography sx={{ fontSize: '12.5px', color: BAD }}>Elariom couldn't collect the replies: {error}</Typography>}
            {!replies && !error && <Working every={7000} lines={['Truckers', 'Forwarders', 'Carrier and airline portals', 'Prices per leg']} />}
            <CoverageView plan={plan} list={list} replies={replies} events={events} />
            <Primary onClick={onCompare} disabled={!replies}>Compare and choose</Primary>
        </Box>
    );
}

// ---------- 7. Compare ----------

const FAMILIES = [
    { id: 'forwarder', label: 'Forwarders · end to end', icon: faGlobe },
    { id: 'ocean', label: 'Direct · sea', icon: faShip },
    { id: 'air', label: 'Direct · air', icon: faPlane },
];

const CMP_COLUMNS = '22px minmax(170px, 1.3fr) repeat(3, minmax(130px, 1fr)) 100px 108px 66px 22px';

function PartCell({ part, timeline }) {
    if (!part) return <Box sx={{ color: alpha(INK, 0.3) }}>—</Box>;
    const s = MAIN(part.kind) && part.offer.schedule;
    return (
        <Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(part.offer.total)}</Box>
            {MAIN(part.kind)
                ? <Box sx={{ margin: '2px 0' }}><CarrierMark name={part.offer.carrier || part.offer.supplier} /></Box>
                : <Box sx={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={part.offer.supplier}>{part.offer.supplier}</Box>}
            {s && <Box sx={{ fontSize: '11px', color: timeline?.missesCutoff ? BAD : INK }} title={`${s.vessel} ${s.voyage || ''}`}><When label='ETD' d={s.etd} /></Box>}
            {part.kind === 'pre-carriage' && timeline?.pickup && <Box sx={{ fontSize: '11px', color: INK }}><When label='Pickup' d={timeline.pickup} /></Box>}
        </Box>
    );
}

// Pickup to delivery, leg by leg, with the dates set by the main leg's departure.
function Timeline({ o }) {
    const t = o.timeline;
    if (!t) return null;
    const part = k => o.parts.find(p => (k === 'main' ? MAIN(p.kind) : p.kind === k));
    const pre = part('pre-carriage');
    const main = part('main');
    const on = part('on-carriage');
    const ship = `${t.vessel}${t.voyage ? ` ${t.voyage}` : ''}`;
    const steps = t.endToEnd
        ? [['ETD', t.etd, <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><CarrierMark name={o.carrier} /> {ship}</Box>], ['ETA', t.eta, ''], ['Delivered', t.delivery, 'estimated']]
        : [
            pre && ['Pickup', t.pickup, `${pre.from} · ${pre.offer.supplier}`],
            pre && ['Cut-off', t.cutoff, pre.to],
            ['ETD', t.etd, <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{main.from} · <CarrierMark name={main.offer.carrier} /> {ship}</Box>],
            ['ETA', t.eta, main.to],
            ['Delivered', t.delivery, on ? `${on.to} · ${on.offer.supplier}` : main.to],
        ].filter(Boolean);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {t.missesCutoff && <Typography sx={{ fontSize: '12px', color: BAD }}>Misses the cut-off from the ready date.</Typography>}
            {steps.map(([label, d, where]) => (
                <Box key={label} sx={{ display: 'grid', gridTemplateColumns: '72px 132px 1fr', gap: '10px', fontSize: '12px' }}>
                    <Box sx={{ color: MUTED }}>{label}</Box>
                    <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}><When d={d} /></Box>
                    <Box>{where}</Box>
                </Box>
            ))}
        </Box>
    );
}

function CompareRow({ o, chosen, onChoose, verdict, recommended }) {
    const [ open, setOpen ] = useState(false);
    const part = k => o.parts.find(p => (k === 'main' ? MAIN(p.kind) : p.kind === k));
    return (
        <Box sx={{ borderTop: dotted, background: chosen ? alpha(GOOD, 0.07) : 'transparent' }}>
            <Box role='button' tabIndex={0} onClick={onChoose} onKeyDown={e => e.key === 'Enter' && onChoose()}
                sx={{ display: 'grid', gridTemplateColumns: CMP_COLUMNS, gap: '10px', padding: '9px 14px', alignItems: 'start', cursor: 'pointer', fontSize: '12.5px' }}>
                <Radio id={`rfq-demo-choice-${o.id}`} size='small' checked={chosen} sx={{ padding: 0 }} />
                <Box>
                    <Box sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>{o.kind === 'end-to-end' ? o.parts[0].offer.supplier : <>{o.routeName?.replace(/^(Ocean|Air)\s+/i, '')} <CarrierMark name={o.carrier} /></>}</Box>
                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '3px' }}>
                        {recommended && <Pill color={GOOD} dot>Recommended</Pill>}
                        {o.tags.map(t => <Pill key={t} color={INK}>{t}</Pill>)}
                    </Box>
                </Box>
                {o.kind === 'end-to-end'
                    ? <Box sx={{ gridColumn: 'span 3' }}>
                        <Box sx={{ color: MUTED, display: 'flex', alignItems: 'center', gap: '6px' }}>All-in {o.carrier && <CarrierMark name={o.carrier} />}</Box>
                        {o.timeline && <Box sx={{ fontSize: '11px' }}><When label='ETD' d={o.timeline.etd} /></Box>}
                    </Box>
                    : <><PartCell part={part('pre-carriage')} timeline={o.timeline} /><PartCell part={part('main')} timeline={o.timeline} /><PartCell part={part('on-carriage')} timeline={o.timeline} /></>}
                <Box sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(o.total)}</Box>
                <Box sx={{ fontVariantNumeric: 'tabular-nums', paddingLeft: '20px' }}>
                    <Box sx={{ fontWeight: 600 }}>{o.timeline ? day(o.timeline.delivery) : '—'}</Box>
                    <Box sx={{ fontSize: '11px', color: MUTED }}>{o.timeline ? fromNow(o.timeline.delivery) : days(o.transitDays)}</Box>
                </Box>
                <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{o.freeDays ? `${o.freeDays} days` : '—'}</Box>
                <IconButton size='small' aria-label='Details' onClick={e => { e.stopPropagation(); setOpen(!open); }} sx={{ padding: '2px' }}>
                    <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', transform: open ? 'rotate(180deg)' : 'none' }} />
                </IconButton>
            </Box>
            <Collapse in={open}>
                <Box sx={{ padding: '4px 14px 14px 46px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {verdict && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '4px 24px', fontSize: '12px' }}>
                            <Box>{verdict.pros.map(p => <Box key={p} sx={{ display: 'flex', gap: '8px' }}><FontAwesomeIcon icon={faPlus} style={{ color: GOOD, fontSize: '10px', marginTop: '4px' }} />{p}</Box>)}</Box>
                            <Box>{verdict.cons.map(p => <Box key={p} sx={{ display: 'flex', gap: '8px' }}><FontAwesomeIcon icon={faMinus} style={{ color: BAD, fontSize: '10px', marginTop: '4px' }} />{p}</Box>)}</Box>
                        </Box>
                    )}
                    <Timeline o={o} />
                    {o.parts.map(p => (
                        <Box key={p.legId} sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{LEG[p.kind]?.label} · {p.offer.supplier} · {money(p.offer.total)}</Typography>
                            <Charges offer={p.offer} />
                            {MAIN(p.kind) || p.kind === 'end-to-end'
                                ? (p.offer.freeTime || p.offer.afterFreeTime) && <Typography sx={{ fontSize: '11.5px', color: MUTED }}>Free time: {p.offer.freeDays ? days(p.offer.freeDays) : p.offer.freeTime}{p.offer.afterFreeTime ? ` · then ${p.offer.afterFreeTime}` : ''}</Typography>
                                : null}
                            {p.offer.conditions && <Typography sx={{ fontSize: '11.5px', color: MUTED }}>Note: {p.offer.conditions}</Typography>}
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

function Charges({ offer }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, auto) auto auto', columnGap: '20px', rowGap: '2px', fontSize: '12px', width: 'fit-content' }}>
                {offer.charges.map((ch, i) => [
                    <Box key={`n${i}`}>{ch.name}</Box>,
                    <Box key={`a${i}`} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{ch.currency} {Number(ch.amount).toLocaleString('en-US')}</Box>,
                    <Box key={`b${i}`} sx={{ color: MUTED }}>{ch.kg ? `${Number(ch.kg).toLocaleString('en-US')} kg × ${ch.ratePerKg}` : ch.basis}</Box>,
                ])}
            </Box>
            {offer.conversion?.length > 0 && <Typography sx={{ fontSize: '11px', color: MUTED }}>{offer.conversion.join(' · ')}</Typography>}
        </Box>
    );
}

function CompareStage({ replies, decision, decisionError, choice, setChoice, onApprove }) {
    const total = replies.comparison.reduce((sum, c) => sum + (c.options.find(o => o.id === choice[c.lane.id])?.total || 0), 0);
    const head = { fontSize: '11px', color: MUTED };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', minHeight: '100%' }}>
            <H>Compare and choose</H>
            {replies.comparison.map(c => {
                const lane = decision?.lanes.find(l => l.laneId === c.lane.id);
                return (
                    <Box key={c.lane.id} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <H2 right={`${c.lane.quantity} × ${c.lane.equipment} · USD`}>{c.lane.name || `${c.lane.origin} → ${c.lane.destination}`}</H2>
                        <Box sx={{ border: `1px solid ${GOOD}`, borderRadius: '8px', background: alpha(GOOD, 0.05), padding: '10px 14px' }}>
                            {lane
                                ? <>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>{lane.headline || lane.decision}</Typography>
                                    {lane.headline && <Expandable title='Why'><Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, marginTop: '6px', maxWidth: '820px' }}>{lane.decision}</Typography></Expandable>}
                                </>
                                : decisionError
                                    ? <Typography sx={{ fontSize: '12.5px', color: BAD }}>Elariom couldn't weigh the options: {decisionError}. The best value is preselected.</Typography>
                                    : <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: MUTED }}><CircularProgress size={12} thickness={5} /> Weighing the options…</Box>}
                        </Box>
                        <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', background: '#FFFFFF', overflowX: 'auto' }}>
                            <Box sx={{ minWidth: '900px' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: CMP_COLUMNS, gap: '10px', padding: '8px 14px' }}>
                                    <Box /><Box sx={head}>Option</Box><Box sx={head}>Origin trucking</Box><Box sx={head}>Main leg</Box><Box sx={head}>Destination trucking</Box><Box sx={{ ...head, textAlign: 'right' }}>Total</Box><Box sx={{ ...head, paddingLeft: '20px' }}>Delivered</Box><Box sx={head}>Free</Box><Box />
                                </Box>
                                {FAMILIES.map(f => {
                                    const list = c.options.filter(o => o.family === f.id);
                                    if (!list.length) return null;
                                    return (
                                        <Box key={f.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 14px', background: SURFACE, borderTop: `1px solid ${HAIRLINE}`, fontSize: '12px', fontWeight: 600 }}>
                                                <FontAwesomeIcon icon={f.icon} style={{ fontSize: '11px' }} /> {f.label}
                                                <Typography component='span' sx={{ fontSize: '11.5px', color: MUTED, fontWeight: 400 }}>· {list.length} · from {money(_.minBy(list, 'total').total)}</Typography>
                                            </Box>
                                            {list.map(o => (
                                                <CompareRow key={o.id} o={o} chosen={choice[c.lane.id] === o.id} recommended={lane?.recommendedId === o.id}
                                                    verdict={lane?.options[o.id]} onChoose={() => setChoice({ ...choice, [c.lane.id]: o.id })} />
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
            {/* Stays in view at the bottom of the popup. */}
            <Box sx={{ position: 'sticky', bottom: '-26px', marginTop: 'auto', background: '#F6F5F1', paddingBlock: '12px', borderTop: `1px solid ${INK}`, display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', zIndex: 1 }}>
                <Box sx={{ flex: 1, minWidth: '240px' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Total buying cost: {money(total)}</Typography>
                </Box>
                <Primary onClick={onApprove} disabled={replies.comparison.some(c => c.options.length && !choice[c.lane.id])}>Approve the choice</Primary>
            </Box>
        </Box>
    );
}

// ---------- 8. Done ----------

// What was booked, as the route in time order (each leg with who runs it and its dates), and the
// suppliers who were not chosen.
function bookingOf({ list, chosen }) {
    const winners = new Set(chosen.flatMap(x => x.option.suppliers));
    const reason = s => (chosen.some(x => x.option.kind === 'direct') && s.type === 'forwarder' ? 'direct legs cheaper'
        : chosen.some(x => x.option.kind === 'end-to-end') && s.type === 'trucker' ? 'job went to a forwarder' : 'higher price');
    const routes = chosen.map(x => {
        const t = x.option.timeline || {};
        return x.option.parts.map(p => {
            const ship = t.vessel ? `${t.vessel}${t.voyage ? ` ${t.voyage}` : ''}` : '';
            if (p.kind === 'pre-carriage') return { kind: p.kind, from: p.from, to: p.to, who: p.offer.supplier, dates: [['Pickup', t.pickup], ['Cut-off', t.cutoff]], note: 'Pickup contacts sent' };
            if (MAIN(p.kind)) return { kind: p.kind, from: p.from, to: p.to, who: p.offer.carrier || p.offer.supplier, carrier: true, dates: [['ETD', t.etd], ['ETA', t.eta]], note: ship ? `${ship} · request on portal` : 'Request on portal' };
            if (p.kind === 'on-carriage') return { kind: p.kind, from: p.from, to: p.to, who: p.offer.supplier, dates: [['Delivery', t.delivery]], note: 'Slot with the DC' };
            return { kind: 'end-to-end', from: (x.lane.name || x.lane.origin).split('→')[0].trim(), to: (x.lane.name || x.lane.destination).split('→').pop().trim(), who: p.offer.supplier, dates: [['ETD', t.etd], ['ETA', t.eta], ['Delivery', t.delivery]], note: [ship, 'pickup contacts sent'].filter(Boolean).join(' · ') };
        });
    });
    const lost = list.filter(s => !winners.has(s.id));
    const notChosen = _.map(_.groupBy(lost, reason), (group, why) => ({ names: group.map(g => g.name), why }));
    return { routes, notChosen };
}

function BookedRoute({ legs, notChosen = [] }) {
    if (!legs?.length) return null;
    const stops = [legs[0].from, ...legs.map(l => l.to)];
    const stop = text => (
        <Box sx={{ alignSelf: 'center', border: `1px solid ${INK}`, borderRadius: '14px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, background: '#FFFFFF', whiteSpace: 'nowrap' }}>{text}</Box>
    );
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ overflowX: 'auto', paddingBottom: '4px' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '8px', width: 'max-content' }}>
                    {stops.map((place, i) => (
                        <Box key={`${place}-${i}`} sx={{ display: 'flex', alignItems: 'stretch', gap: '8px' }}>
                            {stop(place)}
                            {legs[i] && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{ width: '18px', borderTop: `1.5px solid ${INK}` }} />
                                    <Box sx={{ border: `1px solid ${HAIRLINE}`, borderTop: `3px solid ${GOOD}`, borderRadius: '6px', background: '#FFFFFF', padding: '8px 12px', minWidth: '170px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: MUTED }}>
                                            <FontAwesomeIcon icon={LEG[legs[i].kind]?.icon || faGlobe} style={{ fontSize: '10px' }} /> {LEG[legs[i].kind]?.label}
                                        </Box>
                                        <Box sx={{ fontSize: '13px', fontWeight: 600 }}>{legs[i].carrier || isCarrier(legs[i].who) ? <CarrierMark name={legs[i].who} showName /> : legs[i].who}</Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '10px', rowGap: '1px', fontSize: '12px', width: 'fit-content' }}>
                                            {legs[i].dates.filter(([, d]) => d).map(([label, d]) => [
                                                <Box key={`${label}-l`} sx={{ color: MUTED }}>{label}</Box>,
                                                <Box key={`${label}-d`} sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}><When d={d} /></Box>,
                                            ])}
                                        </Box>
                                        {legs[i].note && <Box sx={{ fontSize: '11px', color: MUTED }}>{legs[i].note}</Box>}
                                    </Box>
                                    <Box sx={{ width: '18px', borderTop: `1.5px solid ${INK}`, position: 'relative', '&:after': { content: '""', position: 'absolute', right: '-1px', top: '-4.5px', borderLeft: `6px solid ${INK}`, borderTop: '4px solid transparent', borderBottom: '4px solid transparent' } }} />
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
            {notChosen.map(n => (
                <Typography key={n.why} sx={{ fontSize: '12px', color: MUTED }}>Not chosen: {n.names.join(', ')} · told why: {n.why}</Typography>
            ))}
        </Box>
    );
}

function DoneStage({ list, replies, choice, onClose }) {
    const chosen = replies.comparison.map(c => ({ lane: c.lane, option: c.options.find(o => o.id === choice[c.lane.id]) })).filter(x => x.option);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <H>Booked</H>
            {chosen.map(x => (
                <Facts key={x.lane.id} items={[
                    ['Option', x.option.title],
                    ['Total', money(x.option.total)],
                    ['ETD', x.option.timeline && <When d={x.option.timeline.etd} />],
                    ['Delivered', x.option.timeline && <When d={x.option.timeline.delivery} />],
                    ['Free time', x.option.freeDays ? days(x.option.freeDays) : null],
                ]} />
            ))}
            {(() => {
                const b = bookingOf({ list, chosen });
                return b.routes.map((legs, i) => <BookedRoute key={i} legs={legs} notChosen={i === b.routes.length - 1 ? b.notChosen : []} />);
            })()}
            <Primary onClick={onClose}>Back to the RFQ desk</Primary>
        </Box>
    );
}

// ---------- The RFQ desk ----------

// Other tickets on the desk, for the demo: what a pricing team's queue looks like on a normal day.
const OTHER_TICKETS = [
    { id: '00048214', customer: 'Saigon Home Living', route: 'Ho Chi Minh City → Felixstowe', load: '2 × 40\' HC', received: '09:12', status: 'new', progress: '—', doing: 'Open to start', best: null },
    { id: '00048211', customer: 'Nordic Coffee Roasters', route: 'Santos → Gothenburg (DC)', load: '2 × 40\' Dry', received: 'Yesterday', status: 'followup', progress: '5/7 · all ways priced', doing: 'Auto-reminder sent to 2 · next 14:00', best: 9840 },
    { id: '00048209', customer: 'Medi Pharma Ltd', route: 'Mumbai → Ashdod', load: '1 × 20\' Reefer', received: 'Yesterday', status: 'waiting', progress: '2/6 · trucking unpriced', doing: 'Auto follow-up at 12:00', best: null },
    { id: '00048207', customer: 'Shenzhen Electronics BV', route: 'Shenzhen → Rotterdam', load: 'LCL 12 cbm', received: 'Mon', status: 'ready', progress: '8/8', doing: 'Recommended: SinoLink', best: 2140 },
    { id: '00048204', customer: 'Andes Fruit Co.', route: 'Valparaíso → Antwerp', load: '3 × 40\' Reefer', received: 'Mon', status: 'done', progress: 'direct, MSC', doing: '', best: 21400 },
    { id: '00048202', customer: 'Istanbul Textiles', route: 'Istanbul → Haifa', load: '4 × 40\' HC', received: 'Fri', status: 'done', progress: 'Anatolia Cargo', doing: '', best: 12950 },
];

// What opening an example ticket shows: the compared options of one that is ready to decide, and
// what was booked and logged for the ones that are done.
const EXAMPLE_DETAILS = {
    '00048207': {
        replies: '8/8',
        options: [
            { way: 'forwarder', name: 'SinoLink Logistics', total: 2140, d2d: 31, free: 7, recommended: true, tags: ['Best value'], why: 'one party' },
            { way: 'forwarder', name: 'East Wind Forwarding', total: 2310, d2d: 33, free: 7 },
            { way: 'ocean', name: 'Via Yantian · CMA CGM LCL', origin: [180, 'Shenzhen Haulage', ['Pickup', inDays(5)]], main: [1420, 'CMA CGM', ['ETD', inDays(8)]], dest: [390, 'Maasvlakte Haulage'], total: 1990, d2d: 34, free: 5, tags: ['Cheapest'] },
            { way: 'ocean', name: 'Via Yantian · Maersk LCL', origin: [180, 'Shenzhen Haulage', ['Pickup', inDays(6)]], main: [1560, 'Maersk', ['ETD', inDays(9)]], dest: [390, 'Maasvlakte Haulage'], total: 2130, d2d: 32, free: 5 },
            { way: 'air', name: 'Via Hong Kong · Cathay Cargo', origin: [240, 'Shenzhen Haulage', ['Pickup', inDays(5)]], main: [9850, 'Cathay Cargo', ['ETD', inDays(6)]], dest: [310, 'Schiphol Road Feeding'], total: 10400, d2d: 5, free: 2, tags: ['Fastest'] },
        ],
    },
    '00048204': {
        booked: { name: 'Direct · sea via Valparaíso · MSC', total: 21400, etd: inDays(1), delivered: inDays(33), free: 10 },
        sfAt: 'Mon 11:24',
        legs: [
            { kind: 'pre-carriage', from: 'Los Andes (packhouse)', to: 'Valparaíso', who: 'Transportes Andinos', dates: [['Pickup', inDays(-2)], ['Cut-off', inDays(-1)]], note: 'Pickup contacts sent' },
            { kind: 'ocean', from: 'Valparaíso', to: 'Antwerp', who: 'MSC', carrier: true, dates: [['ETD', inDays(1)], ['ETA', inDays(31)]], note: 'MSC Lorena FX546' },
            { kind: 'on-carriage', from: 'Antwerp', to: 'Ghent (DC)', who: 'Van Moer Logistics', dates: [['Delivery', inDays(33)]], note: 'Slot with the DC' },
        ],
        notChosen: [{ names: ['Andes Cargo', 'Atlas Global Logistics'], why: 'direct legs cheaper' }],
    },
    '00048202': {
        booked: { name: 'Anatolia Cargo · end to end · ZIM', total: 12950, etd: inDays(3), delivered: inDays(12), free: 14 },
        sfAt: 'Fri 16:05',
        legs: [
            { kind: 'end-to-end', from: 'Istanbul (mill)', to: 'Haifa (DC)', who: 'Anatolia Cargo', dates: [['Pickup', inDays(0)], ['ETD', inDays(3)], ['ETA', inDays(10)], ['Delivery', inDays(12)]], note: 'ZIM Antwerp · pickup contacts sent' },
        ],
        notChosen: [{ names: ['Mavi Lojistik'], why: 'higher price' }, { names: ['Levant Freight'], why: 'less free time' }],
    },
};

// The chosen offer, sent and logged back in the customer's Salesforce case (look only, no logo).
function SalesforceLogged({ caseNumber, at }) {
    return (
        <Box sx={{ border: `1px solid ${SF.line}`, borderLeft: `3px solid ${SF.blue}`, borderRadius: '4px', background: '#FFFFFF', padding: '8px 12px', width: 'fit-content', display: 'flex', alignItems: 'center', gap: '8px', '&, & *': { fontFamily: `${SF.font} !important` } }}>
            <Box sx={{ width: '20px', height: '20px', borderRadius: '3px', background: '#F88962', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '10px' }} /></Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: SF.ink }}>Logged in Salesforce</Typography>
            <Typography sx={{ fontSize: '12px', color: SF.muted }}>· Case {caseNumber} · {at}</Typography>
        </Box>
    );
}

function ExampleDetail({ id }) {
    const d = EXAMPLE_DETAILS[id];
    if (!d) return null;
    const cell = v => (v ? <Box><Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(v[0])}</Box>{isCarrier(v[1]) ? <Box sx={{ margin: '2px 0' }}><CarrierMark name={v[1]} /></Box> : <Box sx={{ fontSize: '11px', color: MUTED }}>{v[1]}</Box>}{v[2] && <Box sx={{ fontSize: '11px' }}><When label={v[2][0]} d={v[2][1]} /></Box>}</Box> : <Box sx={{ color: alpha(INK, 0.3) }}>—</Box>);
    if (d.options) {
        const head = { fontSize: '11px', color: MUTED };
        const cols = 'minmax(180px, 1.3fr) repeat(3, minmax(130px, 1fr)) 150px 96px 70px';
        const cheapest = _.minBy(d.options, 'total');
        const fastest = _.minBy(d.options, 'd2d');
        const pick = d.options.find(o => o.recommended);
        const ways = _.uniq(d.options.map(o => o.way)).length;
        const nameOf = o => (o.way === 'air' ? `${o.name.split('·').pop().trim()} air` : o.name);
        // The decision at a glance: how many options, the cheapest, the fastest and the one recommended.
        const summary = [
            { label: 'Options compared', value: d.options.length, sub: `${ways} ways · ${d.replies} replies` },
            { label: 'Cheapest', value: money(cheapest.total), sub: `${nameOf(cheapest)} · ${days(cheapest.d2d)}` },
            { label: 'Fastest', value: money(fastest.total), sub: `${nameOf(fastest)} · ${days(fastest.d2d)}` },
            pick && { label: 'Recommended', value: money(pick.total), sub: `${pick.name} · ${days(pick.d2d)} · ${pick.why}`, good: true },
        ].filter(Boolean);
        return (
            <Box sx={{ padding: '4px 16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${summary.length}, minmax(0, 1fr))`, gap: '10px' }}>
                    {summary.map(x => (
                        <Box key={x.label} sx={{ border: `1px solid ${x.good ? GOOD : HAIRLINE}`, borderRadius: '8px', background: x.good ? alpha(GOOD, 0.06) : '#FFFFFF', padding: '10px 14px', minWidth: 0 }}>
                            <Label>{x.label}</Label>
                            <Box sx={{ fontSize: '17px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: x.good ? GOOD : INK, margin: '2px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.value}</Box>
                            <Box sx={{ fontSize: '11.5px', color: MUTED }}>{x.sub}</Box>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', background: '#FFFFFF', overflowX: 'auto' }}>
                    <Box sx={{ minWidth: '880px' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: '10px', padding: '8px 14px' }}>
                            <Box sx={head}>Option</Box><Box sx={head}>Origin trucking</Box><Box sx={head}>Main leg</Box><Box sx={head}>Destination trucking</Box><Box sx={{ ...head, textAlign: 'right' }}>Total</Box><Box sx={{ ...head, paddingLeft: '20px' }}>Transit</Box><Box sx={head}>Free</Box>
                        </Box>
                        {FAMILIES.map(f => {
                            const list = _.sortBy(d.options.filter(o => o.way === f.id), 'total');
                            return list.length ? (
                                <Box key={f.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 14px', background: SURFACE, borderTop: `1px solid ${HAIRLINE}`, fontSize: '12px', fontWeight: 600 }}>
                                        <FontAwesomeIcon icon={f.icon} style={{ fontSize: '11px' }} /> {f.label}
                                        <Typography component='span' sx={{ fontSize: '11.5px', color: MUTED, fontWeight: 400 }}>· {list.length} · from {money(list[0].total)}</Typography>
                                    </Box>
                                    {list.map(o => (
                                        <Box key={o.name} sx={{ display: 'grid', gridTemplateColumns: cols, gap: '10px', padding: '9px 14px', borderTop: dotted, fontSize: '12.5px', alignItems: 'start', background: o.recommended ? alpha(GOOD, 0.07) : 'transparent', boxShadow: o.recommended ? `inset 3px 0 0 ${GOOD}` : 'none' }}>
                                            <Box>
                                                <Box sx={{ fontWeight: 600 }}>{o.name}</Box>
                                                <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '3px' }}>
                                                    {o.recommended && <Pill color={GOOD} dot>Recommended</Pill>}
                                                    {(o.tags || []).map(t => <Pill key={t} color={INK}>{t}</Pill>)}
                                                </Box>
                                            </Box>
                                            {o.way === 'forwarder' ? <Box sx={{ gridColumn: 'span 3', color: MUTED }}>All-in</Box> : <>{cell(o.origin)}{cell(o.main)}{cell(o.dest)}</>}
                                            <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                <Box sx={{ fontWeight: 700, fontSize: '13.5px' }}>{money(o.total)}</Box>
                                                <Box sx={{ fontSize: '11px', color: o === cheapest ? GOOD : MUTED, whiteSpace: 'nowrap' }}>{o === cheapest ? 'Cheapest' : `+${money(o.total - cheapest.total)} vs cheapest`}</Box>
                                            </Box>
                                            <Box sx={{ paddingLeft: '20px' }}>
                                                <Box>{days(o.d2d)}</Box>
                                                <Box sx={{ height: '4px', borderRadius: '2px', background: alpha(INK, 0.08), marginTop: '5px', width: '64px' }}>
                                                    <Box sx={{ height: '100%', borderRadius: '2px', background: o === fastest ? GOOD : alpha(INK, 0.35), width: `${Math.round(100 * o.d2d / _.maxBy(d.options, 'd2d').d2d)}%` }} />
                                                </Box>
                                            </Box>
                                            <Box>{days(o.free)}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            ) : null;
                        })}
                    </Box>
                </Box>
            </Box>
        );
    }
    return (
        <Box sx={{ padding: '4px 16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Facts items={[['Booked', d.booked.name], ['Total', money(d.booked.total)], ['ETD', <When d={d.booked.etd} />], ['Delivered', <When d={d.booked.delivered} />], ['Free time', days(d.booked.free)]]} />
            <BookedRoute legs={d.legs} notChosen={d.notChosen} />
            <SalesforceLogged caseNumber={id} at={d.sfAt} />
        </Box>
    );
}


const STATUS = {
    new: { label: 'New', color: MUTED },
    reading: { label: 'Drafting', color: INK },
    approval: { label: 'To approve', color: WARN },
    waiting: { label: 'Waiting', color: INK },
    followup: { label: 'Following up', color: WARN },
    ready: { label: 'Ready to decide', color: GOOD },
    done: { label: 'Booked', color: GOOD },
};

const DESK_COLUMNS = '98px minmax(170px, 1.3fr) 110px 76px 160px minmax(170px, 1.2fr) minmax(200px, 1.6fr) 100px';

function DeskRow({ t, onOpen, live, children, first, expanded, onToggle }) {
    const st = STATUS[t.status];
    return (
        <Box sx={{ borderTop: first ? 'none' : `1px solid ${HAIRLINE}`, background: live ? '#FFFFFF' : 'transparent' }}>
            <Box role='button' tabIndex={0} onClick={onOpen} onKeyDown={e => e.key === 'Enter' && onOpen?.()}
                sx={{ display: 'grid', gridTemplateColumns: DESK_COLUMNS, gap: '12px', padding: '12px 16px', alignItems: 'start', fontSize: '12.5px', cursor: onOpen ? 'pointer' : 'default', '&:hover': onOpen ? { background: alpha(INK, 0.03) } : {} }}>
                <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: live ? 600 : 400, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {onToggle
                        ? <IconButton size='small' aria-label={expanded ? 'Fold' : 'Unfold'} onClick={e => { e.stopPropagation(); onToggle(); }} sx={{ padding: '2px', marginLeft: '-4px' }}>
                            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
                        </IconButton>
                        : <Box sx={{ width: '18px' }} />}
                    {t.id}
                </Box>
                <Box><Box sx={{ fontWeight: 600 }}>{t.customer}</Box><Box sx={{ fontSize: '11.5px', color: MUTED }}>{t.route}</Box></Box>
                <Box>{t.load}</Box>
                <Box sx={{ color: MUTED }}>{t.received}</Box>
                <Box><Pill color={st.color} dot>{st.label}</Pill></Box>
                <Box>{t.progress}</Box>
                <Box sx={{ color: t.status === 'followup' ? WARN : MUTED, display: 'flex', gap: '6px' }}>
                    {(t.status === 'followup' || t.status === 'waiting') && <FontAwesomeIcon icon={t.status === 'followup' ? faBell : faClock} style={{ fontSize: '11px', marginTop: '3px' }} />}
                    <Box>{t.doing}</Box>
                </Box>
                <Box sx={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{t.best != null ? money(t.best) : '—'}</Box>
            </Box>
            <Collapse in={!!expanded}>{children}</Collapse>
        </Box>
    );
}

function RfqDesk({ live, liveExtra, onOpenLive, onClose }) {
    const [ liveOpen, setLiveOpen ] = useState(true);
    const [ openIds, setOpenIds ] = useState([]);
    const toggle = id => setOpenIds(o => (o.includes(id) ? o.filter(x => x !== id) : [...o, id]));
    const tickets = [live, ...OTHER_TICKETS];
    const counts = _.countBy(tickets, 'status');
    const head = { fontSize: '11px', color: MUTED };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 24px', borderBottom: `1px solid ${HAIRLINE}` }}>
                <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '22px', fontWeight: 600 }}>RFQ desk</Typography>
                <Pill color={MUTED} dashed>Demo</Pill>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '11.5px', color: MUTED, display: { xs: 'none', md: 'block' } }}>First ticket is live; the rest are examples.</Typography>
                <IconButton size='small' aria-label='Close' onClick={onClose}><FontAwesomeIcon icon={faXmark} /></IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
                    {['new', 'waiting', 'followup', 'ready', 'done'].map(k => (
                        <Box key={k}>
                            <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '26px', fontWeight: 600, lineHeight: 1 }}>{counts[k] || 0}</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: MUTED }}>{STATUS[k].label}</Typography>
                        </Box>
                    ))}
                    <Box sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ border: `1px solid ${HAIRLINE}`, borderRadius: '8px', overflowX: 'auto', background: SURFACE }}>
                    <Box sx={{ minWidth: '1080px' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: DESK_COLUMNS, gap: '12px', padding: '9px 16px', borderBottom: `1px solid ${HAIRLINE}` }}>
                            <Box sx={head}>Ticket</Box><Box sx={head}>Customer · route</Box><Box sx={head}>Load</Box><Box sx={head}>Received</Box><Box sx={head}>Status</Box><Box sx={head}>Replies</Box><Box sx={head}>Next</Box><Box sx={{ ...head, textAlign: 'right' }}>Best price</Box>
                        </Box>
                        <DeskRow t={live} live first onOpen={onOpenLive} expanded={liveOpen && !!liveExtra} onToggle={liveExtra ? () => setLiveOpen(!liveOpen) : null}>{liveExtra}</DeskRow>
                        {OTHER_TICKETS.map(t => (EXAMPLE_DETAILS[t.id]
                            ? <DeskRow key={t.id} t={t} onOpen={() => toggle(t.id)} expanded={openIds.includes(t.id)} onToggle={() => toggle(t.id)}><ExampleDetail id={t.id} /></DeskRow>
                            : <DeskRow key={t.id} t={t} />))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// ---------- The run ----------

export default function ElariomRfqDemo({ open, onClose, org, accesstoken }) {
    const [ caseOpen, setCaseOpen ] = useState(false);
    const [ stage, setStage ] = useState('ticket');
    const [ reached, setReached ] = useState(0);
    const [ ticket, setTicket ] = useState(SAMPLE_TICKET);
    const [ plan, setPlan ] = useState(null);
    const [ understood, setUnderstood ] = useState(null);
    const [ shownLog, setShownLog ] = useState(0);
    const [ included, setIncluded ] = useState([]);
    const [ drafts, setDrafts ] = useState({});
    const [ sent, setSent ] = useState({});
    const [ sentAt, setSentAt ] = useState(null);
    const [ searched, setSearched ] = useState({});
    const [ replies, setReplies ] = useState(null);
    const [ events, setEvents ] = useState({ online: false, arrived: [], reminded: false, lateReplied: false });
    const [ decision, setDecision ] = useState(null);
    const [ decisionError, setDecisionError ] = useState(null);
    const [ choice, setChoice ] = useState({});
    const [ busy, setBusy ] = useState(false);
    const [ error, setError ] = useState(null);
    const startedAt = useRef(null);
    const run = useRef(0);

    const api = path => `/api/admin/elariom/${org.id}/rfq-demo/${path}?accesstoken=${accesstoken}`;
    const go = id => { setStage(id); setReached(r => Math.max(r, STAGES.findIndex(s => s.id === id))); };
    const list = plan ? (plan.panel || plan.suppliers).filter(s => included.includes(s.id)) : [];

    // Ticked on top of the recommendation: draft their RFQs before showing the drafts.
    async function toDrafts() {
        const id = run.current;
        const missing = list.filter(s => !drafts[s.id]);
        if (missing.length) {
            setBusy(true);
            try {
                const { data } = await axios.post(api('draft'), { request: plan.request, reference: plan.reference, suppliers: missing.map(s => ({ id: s.id, askFor: s.askFor })) });
                if (id !== run.current) return;
                setDrafts(d => ({ ...d, ...Object.fromEntries(data.rfqs.map(r => [r.supplierId, { subject: r.subject, body: r.body }])) }));
            } catch (e) {
                if (id === run.current) setError(errorOf(e));
            }
            if (id !== run.current) return;
            setBusy(false);
        }
        go('draft');
    }

    function reset() {
        run.current += 1;
        setStage('ticket'); setReached(0); setPlan(null); setUnderstood(null); setShownLog(0); setIncluded([]); setDrafts({}); setSent({}); setSentAt(null); setSearched({});
        setReplies(null); setEvents({ online: false, arrived: [], reminded: false, lateReplied: false });
        setDecision(null); setDecisionError(null); setChoice({}); setBusy(false); setError(null);
    }

    async function understand() {
        const id = ++run.current;
        startedAt.current = Date.now();
        go('understand');
        setBusy(true);
        setError(null);
        setUnderstood(null);
        setShownLog(0);
        try {
            // The sample case is read ahead of time; an edited one is read now.
            const read = ticket.trim() === SAMPLE_TICKET.trim()
                ? SAMPLE_READING
                : (await axios.post(api('understand'), { ticket })).data;
            if (id !== run.current) return;
            setUnderstood(read);
            // Choosing and drafting starts now; the log plays out meanwhile.
            const planning = axios.post(api('plan'), { request: read.request });
            for (let i = 1; i <= read.log.length; i++) {
                await pause(400);
                if (id !== run.current) return;
                setShownLog(i);
            }
            const { data } = await planning;
            if (id !== run.current) return;
            setPlan({ ...data, log: read.log });
            go('choose');
            setIncluded(data.suppliers.map(s => s.id));
            setDrafts(Object.fromEntries(data.rfqs.map(r => [r.supplierId, { subject: r.subject, body: r.body }])));
        } catch (e) {
            if (id === run.current) setError(errorOf(e));
        }
        if (id === run.current) setBusy(false);
    }

    async function approveAndSend() {
        const id = run.current;
        go('send');
        const chosenPlan = { ...plan, suppliers: list };
        // Ask for the replies while the sending plays out.
        const pending = axios.post(api('replies'), { plan: chosenPlan }).then(r => r.data);
        for (const s of list) {
            await pause(350);
            if (id !== run.current) return;
            setSent(x => ({ ...x, [s.id]: dayjs().format('HH:mm') }));
        }
        for (const c of plan.carriers) {
            await pause(150);
            if (id !== run.current) return;
            setSearched(x => ({ ...x, [c.name]: true }));
        }
        await pause(800);
        if (id !== run.current) return;
        // Back to the desk: the replies come in there.
        setSentAt(dayjs().format('HH:mm'));
        go('replies');
        setCaseOpen(false);
        let data;
        try {
            data = await pending;
        } catch (e) {
            if (id === run.current) setError(errorOf(e));
            return;
        }
        if (id !== run.current) return;
        setReplies(data);
        setChoice(Object.fromEntries(data.comparison.filter(c => c.best).map(c => [c.lane.id, c.best])));
        // Elariom weighs the options while the replies play out.
        axios.post(api('decision'), { plan: chosenPlan, comparison: data.comparison, startedAt: startedAt.current })
            .then(r => {
                if (id !== run.current) return;
                setDecision(r.data);
                setChoice(ch => ({ ...ch, ...Object.fromEntries(r.data.lanes.map(l => [l.laneId, l.recommendedId])) }));
            })
            .catch(e => { if (id === run.current) setDecisionError(errorOf(e)); });
        await pause(800);
        if (id !== run.current) return;
        setEvents(e => ({ ...e, online: true }));
        // Replies arrive in the order the suppliers usually answer; the late one after a reminder.
        const order = [...list].filter(s => s.id !== data.late.id).sort((a, b) => a.replyHours - b.replyHours);
        for (const s of order) {
            await pause(1400);
            if (id !== run.current) return;
            setEvents(e => ({ ...e, arrived: [...e.arrived, s.id] }));
        }
        await pause(1500);
        if (id !== run.current) return;
        setEvents(e => ({ ...e, reminded: true }));
        await pause(4000);
        if (id !== run.current) return;
        setEvents(e => ({ ...e, arrived: [...e.arrived, data.late.id], lateReplied: true }));
    }

    // The live ticket's row on the desk.
    const lanes = plan && sentAt ? coverageOf({ plan, list, replies, events }) : [];
    const answered = events.arrived.length;
    const bestSoFar = lanes.length ? _.min(lanes.flatMap(l => l.ways.map(w => w.total)).filter(x => x != null)) : null;
    const chosenOptions = replies ? replies.comparison.map(c => c.options.find(o => o.id === choice[c.lane.id])).filter(Boolean) : [];
    let status = 'new';
    let progress = '—';
    let doing = 'Open to start';
    if (stage === 'done') {
        status = 'done';
        progress = `${chosenOptions.map(o => (o.kind === 'end-to-end' ? o.parts[0].offer.supplier : `direct, ${o.carrier}`)).join('; ')}`;
        doing = '';
    } else if (sentAt) {
        const waitingOn = _.uniqBy(lanes.flatMap(l => l.waiting), 'id');
        progress = `${answered}/${list.length}${lanes.every(l => l.allPriced) && replies ? ' · all ways priced' : ''}`;
        if (events.lateReplied) { status = 'ready'; doing = 'Compare and choose'; }
        else if (events.reminded) { status = 'followup'; doing = `Auto-reminder sent to ${waitingOn.map(w => w.name).join(', ')}`; }
        else { status = 'waiting'; doing = `Sent ${sentAt} · auto follow-up on`; }
    } else if (plan) {
        status = 'approval'; progress = `${list.length} RFQs drafted`; doing = 'Approve the RFQs';
    } else if (stage !== 'ticket') {
        status = 'reading'; progress = '—'; doing = 'Drafting RFQs';
    }
    const live = { id: CASE.number, customer: CASE.account, route: 'Tatuí → Ridderkerk (DC)', load: '4 × 40\' HC', received: 'Just now', status, progress, doing, best: stage === 'done' ? _.sumBy(chosenOptions, 'total') : bestSoFar };

    const openCase = () => {
        if (stage === 'replies' && events.lateReplied) go('compare');
        setCaseOpen(true);
    };
    const liveExtra = sentAt ? (
        <Box sx={{ padding: '4px 16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {stage === 'done'
                ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {(() => {
                        const b = bookingOf({ list, chosen: replies.comparison.map(c => ({ lane: c.lane, option: c.options.find(o => o.id === choice[c.lane.id]) })).filter(x => x.option) });
                        return b.routes.map((legs, i) => <BookedRoute key={i} legs={legs} notChosen={i === b.routes.length - 1 ? b.notChosen : []} />);
                    })()}
                    <SalesforceLogged caseNumber={CASE.number} at={dayjs().format('HH:mm')} />
                </Box>
                : <>
                    {error && <Typography sx={{ fontSize: '12.5px', color: BAD }}>Elariom couldn't collect the replies: {error}</Typography>}
                    {!replies && !error && <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: MUTED }}><CircularProgress size={12} thickness={5} /> Replies coming in…</Box>}
                    <CoverageView plan={plan} list={list} replies={replies} events={events} />
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Primary onClick={() => { go('compare'); setCaseOpen(true); }} disabled={!replies}>Compare and choose</Primary>
                        <Button size='small' onClick={() => { setStage('replies'); setCaseOpen(true); }} sx={{ textTransform: 'none' }}>Open the case</Button>
                    </Box>
                </>}
        </Box>
    ) : null;

    const index = STAGES.findIndex(s => s.id === stage);
    let body;
    if (stage === 'ticket') body = <TicketStage ticket={ticket} setTicket={setTicket} workspaceTitle={org?.title} onRun={understand} />;
    else if (!plan) {
        body = error
            ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}><Typography sx={{ fontSize: '13px', color: BAD }}>Elariom couldn't read the request: {error}</Typography><Primary onClick={understand}>Try again</Primary></Box>
            : <><H>Reading the case</H><PlanProgress understood={understood} shownLog={shownLog} /></>;
    }
    else if (stage === 'understand') body = <><H>How Elariom read it</H><PlanProgress understood={understood} shownLog={shownLog} finished /></>;
    else if (stage === 'choose') body = <ChooseStage plan={plan} included={included} setIncluded={setIncluded} onNext={toDrafts} busy={busy} />;
    else if (stage === 'draft') body = <DraftStage plan={plan} included={included} drafts={drafts} setDrafts={setDrafts} onApprove={approveAndSend} />;
    else if (stage === 'send') body = <SendStage list={list} carriers={plan.carriers} sent={sent} searched={searched} />;
    else if (stage === 'replies') body = <RepliesStage plan={plan} list={list} replies={replies} events={events} error={error} onCompare={() => go('compare')} />;
    else if (stage === 'compare') body = <CompareStage replies={replies} decision={decision} decisionError={decisionError} choice={choice} setChoice={setChoice} onApprove={() => go('done')} />;
    else if (stage === 'done') body = <DoneStage list={list} replies={replies} choice={choice} onClose={() => setCaseOpen(false)} />;

    // Going back is allowed to anything already shown, except into the steps before sending once
    // the RFQs are out (that would send again).
    const canOpen = i => plan && i <= reached && !busy && i !== 4 && !(index >= 4 && i < 4);

    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { background: '#F6F5F1' } }}>
            <RfqDesk live={live} liveExtra={liveExtra} onOpenLive={openCase} onClose={onClose} />
            <Dialog open={caseOpen} onClose={() => setCaseOpen(false)} fullWidth maxWidth='lg' PaperProps={{ sx: { height: '92vh', background: '#F6F5F1', borderRadius: '12px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderBottom: `1px solid ${HAIRLINE}` }}>
                    <Typography className='elariom-serif' sx={{ fontFamily: SERIF, fontSize: '20px', fontWeight: 600, whiteSpace: 'nowrap' }}>Case {CASE.number}</Typography>
                    <Pill color={MUTED} dashed>Demo run</Pill>
                    {plan && <Typography sx={{ fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{plan.reference}</Typography>}
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: '11.5px', color: MUTED, display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>Demo: suppliers, rates and replies are simulated. Nothing is sent.</Typography>
                    {stage !== 'ticket' && <Button size='small' onClick={reset} sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>Start over</Button>}
                    <IconButton size='small' aria-label='Back to the RFQ desk' onClick={() => setCaseOpen(false)}><FontAwesomeIcon icon={faXmark} /></IconButton>
                </Box>
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <Box sx={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${HAIRLINE}`, padding: '18px 0', display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', overflowY: 'auto' }}>
                        {STAGES.map((s, i) => {
                            const isNow = i === index;
                            const isDone = i < index || (i <= reached && !isNow);
                            return (
                                <Box
                                    key={s.id}
                                    role='button'
                                    tabIndex={canOpen(i) ? 0 : -1}
                                    onClick={() => canOpen(i) && setStage(s.id)}
                                    sx={{
                                        display: 'flex', gap: '10px', padding: '8px 18px', alignItems: 'flex-start',
                                        cursor: canOpen(i) ? 'pointer' : 'default',
                                        background: isNow ? alpha(INK, 0.06) : 'transparent',
                                        borderLeft: `2px solid ${isNow ? INK : 'transparent'}`,
                                    }}
                                >
                                    <Box sx={{
                                        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '1px', fontSize: '10px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: `1px solid ${isDone ? GOOD : isNow ? INK : HAIRLINE}`,
                                        background: isDone ? GOOD : 'transparent', color: isDone ? '#FFFFFF' : isNow ? INK : MUTED,
                                    }}>
                                        {isDone ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: isNow ? 600 : 400, color: i <= reached ? INK : MUTED }}>{s.label}</Typography>
                                        <Typography sx={{ fontSize: '10.5px', color: MUTED }}>{s.kind}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', padding: { xs: '20px 16px', sm: '26px 32px' }, display: 'flex', flexDirection: 'column' }}>
                        {body}
                    </Box>
                </Box>
            </Dialog>
        </Dialog>
    );
}
